import * as angular from 'angular';

import { NavigationService } from 'services/navigation';
import { AuthenticationService } from 'services/authentication';
import { StateService } from '@uirouter/angularjs';
import * as _ from 'lodash';

import { Experiments } from 'services/experiments';
import { LoggingService } from 'services/logging';
import * as moment from 'moment';

export const module = angular.module('hippoApiService', []);

export interface HippoConfigurationLicenses {
    [STATE_CARRIER: string]: string;
}

interface HippoConfiguration {
    licenses: HippoConfigurationLicenses;
    supported_states: Array<string>;
    smart_home_pro_monitoring_states: Array<string>;
    smart_home_starter_kit_states: Array<string>;
    experiments: Experiments;
}

interface CreateInvoiceResponse {
    url: string;
}

export interface IntercomIdentityVerificationHashResponse {
    customerId: string;
    hash: string;
}

export class HippoApiService {
    private $http: angular.IHttpService;
    private $q: angular.IQService;
    private $state: StateService;
    private navigationService: NavigationService;
    private authenticationService: AuthenticationService;
    private $window: angular.IWindowService;
    private readonly $cookies: angular.cookies.ICookiesService;
    private readonly $location: angular.ILocationService;
    private readonly loggingService: LoggingService;

    constructor(
        $http: angular.IHttpService,
        authenticationService: AuthenticationService,
        $state: StateService,
        $q: angular.IQService,
        navigationService: NavigationService,
        $window: angular.IWindowService,
        $cookies: angular.cookies.ICookiesService,
        $location: angular.ILocationService,
        loggingService: LoggingService
    ) {
        this.$http = $http;
        this.authenticationService = authenticationService;
        this.$state = $state;
        this.$q = $q;
        this.navigationService = navigationService;
        this.$window = $window;
        this.$cookies = $cookies;
        this.$location = $location;
        this.loggingService = loggingService;
    }

    private getCookieDomain() {
        const host = this.$location.host();
        const hostHasSubdomain = host.replace(/[^.]/g, '').length > 1;

        if (host === 'localhost') {
            return host;
        } else if (!hostHasSubdomain) {
            // localhost also doesn't have a subdomain, but I'm keeping it separate for clarity
            return host;
        } else {
            return host.substr(host.indexOf('.')); /* strip off the subdomain */
        }
    }

    private setDeepLinkingUrlForReactApp(url: string) {
        try {
            this.$cookies.put('angular_deep_linking_url', url, {
                path: '/',
                domain: this.getCookieDomain(),
                expires: moment()
                    .add(2, 'minutes')
                    .toDate()
            });
        } catch (e) {
            this.loggingService.log('error setting cookie', e);
        }
    }

    private response401Interceptor<T>(response: angular.IHttpResponse<T>, alwaysReject: boolean = false) {
        if (response.status === 401 && !this.authenticationService.getIsAuthenticating()) {
            this.authenticationService.logout();

            if (this.$state.$current.name !== 'login') {
                this.setDeepLinkingUrlForReactApp(this.$window.location.pathname);
                this.$window.localStorage.setItem('redirect_url', this.$window.location.href);
                this.navigationService.toLoginPage();
            }

            return this.$q.reject(response);
        }

        if (alwaysReject) {
            return this.$q.reject(response);
        } else {
            return response;
        }
    }

    private success401Interceptor<T>(response: angular.IHttpResponse<T>) {
        return this.response401Interceptor<T>(response);
    }

    private error401Interceptor<T>(response: angular.IHttpResponse<T>) {
        return this.response401Interceptor<T>(response, true);
    }

    getApiBaseUrl() {
        const apiVersion = '/v1/';

        if (this.$window.appConfig && this.$window.appConfig.API_HOST) {
            return this.$window.appConfig.API_HOST + apiVersion;
        }

        let port = '';
        let hostPrefix = '';

        if (this.$window.host === 'myhippo.com') {
            hostPrefix = 'api.';
        } else if (this.$window.location.port) {
            port = ':3000';
        } else {
            hostPrefix = 'api-';
        }

        const location = this.$window.location;

        const protocol = location.protocol;
        const hostname = location.hostname;

        return `${protocol}//${hostPrefix}${hostname}${port}${apiVersion}`;
    }

    get<T>(path: string, intercept401s: boolean = true): angular.IPromise<angular.IHttpResponse<T>> {
        const request = this.$http<T>({
            method: 'GET',
            url: this.getApiBaseUrl() + path,
            headers: {
                authorization: 'Bearer ' + this.authenticationService.getAuthenticationToken()
            }
        });

        if (intercept401s) {
            return request
                .then((response) => {
                    return this.success401Interceptor(response);
                })
                .catch((response) => {
                    return this.error401Interceptor(response);
                });
        } else {
            return request;
        }
    }

    post<T>(path: string, data?: any): angular.IPromise<angular.IHttpResponse<T>> {
        return this.$http<T>({
            method: 'POST',
            url: this.getApiBaseUrl() + path,
            headers: {
                authorization: 'Bearer ' + this.authenticationService.getAuthenticationToken()
            },
            data: data
        })
            .then((response) => {
                return this.success401Interceptor(response);
            })
            .catch((response) => {
                return this.error401Interceptor(response);
            });
    }

    getConfiguration() {
        return this.get<HippoConfiguration>('configuration');
    }

    getExperiments() {
        return (this.getConfiguration().then(function(response: angular.IHttpResponse<HippoConfiguration>) {
            return _.get(response, 'data') && _.isPlainObject(response.data.experiments)
                ? response.data.experiments
                : {};
        }) as angular.IPromise<any>) as angular.IPromise<Experiments>;
    }

    getStateLicenses() {
        return (this.getConfiguration().then(function(response: angular.IHttpResponse<HippoConfiguration>) {
            return response.data.licenses;
        }) as angular.IPromise<any>) as angular.IPromise<HippoConfigurationLicenses>;
    }

    getProMonitoringStates() {
        return (this.getConfiguration().then(function(response: angular.IHttpResponse<HippoConfiguration>) {
            return response.data.smart_home_pro_monitoring_states;
        }) as angular.IPromise<any>) as angular.IPromise<HippoConfigurationLicenses>;
    }

    getCustomerPolicyInfos() {
        return this.get<Array<CustomerPolicyInfo>>('customer/policies');
    }

    verifyAuthentication() {
        return this.get<undefined>('customer/verify', false);
    }

    updateCustomerPolicy(policyId: PolicyId, policyInfoUpdate: CustomerPolicyInfoUpdate) {
        return this.post<CustomerPolicyInfo>(`customer/policies/${policyId}`, policyInfoUpdate);
    }

    updateFrequencyOption(policyId: PolicyId, frequencyOption: FrequencyOptions) {
        return this.post<CustomerPolicyInfo>(`customer/policies/${policyId}/add-charge`, frequencyOption);
    }

    contactRequest(policyId: PolicyId, reason: string, preferredMethodOfCommunication: string) {
        return this.post<undefined>(`customer/policies/${policyId}/contact-request`, {
            reason,
            preferredMethodOfCommunication
        });
    }

    rechargePayment(policyId: PolicyId) {
        return this.post<CustomerPolicyInfo>(`customer/policies/${policyId}/recharge-payment`);
    }

    addCharge(policyId: PolicyId, addChargeOptions: AddChargeOptions) {
        return this.post<CustomerPolicyInfo>(`customer/policies/${policyId}/add-charge`, addChargeOptions);
    }

    calculateDiscount(policyId: PolicyId, field: string) {
        return this.get<SmartHomeDiscount>(`customer/policies/${policyId}/calculate-discount/?field=${field}`);
    }

    createInvoice(policyId: PolicyId, payment: Payment) {
        return this.post<CreateInvoiceResponse>(`customer/policies/${policyId}/create-invoice`, payment);
    }

    getIntercomIdentityVerificationHash() {
        return this.get<IntercomIdentityVerificationHashResponse>('customer/intercom-identity-verification-hash');
    }

    // internalTracking(eventData: InternalTrackEvent) {
    //     return this.post<object>('track', eventData);
    // }
}

module.factory('hippoApiService', function(
    $http: angular.IHttpService,
    $state: StateService,
    $q: angular.IQService,
    authenticationService: AuthenticationService,
    navigationService: NavigationService,
    $window: angular.IWindowService,
    $cookies: angular.cookies.ICookiesService,
    $location: angular.ILocationService,
    loggingService: LoggingService
) {
    'ngInject';

    return new HippoApiService($http, authenticationService, $state, $q, navigationService, $window, $cookies, $location, loggingService);
});
